/*
 * Scroll-linked motion, no dependencies. Every [data-motion="a b"] element gets --p, 0 → 1,
 * while it travels from a to b: 0 is "its top meets the viewport bottom", 1 is "its bottom
 * meets the viewport top". Both ends are clamped to the page's scroll range, so motion in
 * the first screen starts at 0 and motion at the end of the page can finish. Elements near
 * the viewport also get .is-near. CSS does the moving (transforms) under html.motion-ready;
 * with reduced motion this does nothing and the CSS shows every final state.
 */
(() => {
  'use strict';

  if (window.themeMotion) return;

  const root = document.documentElement;
  const reduce = matchMedia('(prefers-reduced-motion: reduce)');
  const near = new Set();
  let items = [];
  let io = null;
  let raf = 0;
  let full = true;
  let on = false;

  const range = (el) => {
    const [a, b] = (el.dataset.motion || '').trim().split(/[\s,]+/).map(parseFloat);
    return [Number.isFinite(a) ? a : 0, Number.isFinite(b) ? b : 1];
  };

  const tick = () => {
    raf = 0;
    const list = full ? items : [...near];
    full = false;
    const vh = innerHeight;
    const y = scrollY;
    const max = Math.max(0, root.scrollHeight - vh);
    const fit = (value) => Math.min(Math.max(value, 0), max);
    // Read every position first, then write, so layout is computed once per frame.
    const values = list.map((el) => {
      const rect = el.getBoundingClientRect();
      const [a, b] = el.motionRange || (el.motionRange = range(el));
      const top = rect.top + y - vh;
      const span = vh + rect.height;
      const from = fit(top + a * span);
      const to = fit(top + b * span);
      const p = to > from ? (y - from) / (to - from) : Number(y >= to);
      return Math.min(Math.max(p, 0), 1).toFixed(3);
    });
    list.forEach((el, i) => {
      if (el.motionP !== values[i]) el.style.setProperty('--p', (el.motionP = values[i]));
    });
  };

  const schedule = (all) => {
    if (!on) return;
    if (all) full = true;
    if (!raf) raf = requestAnimationFrame(tick);
  };

  // A full pass on every change settles anything that skipped past its range at 0 or 1.
  const onView = (entries) => {
    entries.forEach(({ target, isIntersecting }) => {
      near[isIntersecting ? 'add' : 'delete'](target);
      target.classList.toggle('is-near', isIntersecting);
    });
    schedule(true);
  };

  const scan = () => {
    if (io) io.disconnect();
    near.clear();
    items = [...document.querySelectorAll('[data-motion]')];
    items.forEach((el) => {
      el.motionRange = null;
    });
    if (!on) return;
    io = new IntersectionObserver(onView, { rootMargin: '25% 0px' });
    items.forEach((el) => io.observe(el));
    full = true;
    tick(); // first values before the next paint
  };

  const sync = () => {
    on = !reduce.matches && 'IntersectionObserver' in window;
    root.classList.toggle('motion-ready', on);
    cancelAnimationFrame(raf);
    raf = 0;
    scan();
  };

  addEventListener('scroll', () => near.size && schedule(), { passive: true });
  addEventListener('resize', () => schedule(true), { passive: true });
  addEventListener('load', () => schedule(true));
  reduce.addEventListener('change', sync);
  // Theme editor: sections are re-rendered, added, removed and reordered in place.
  ['shopify:section:load', 'shopify:section:unload', 'shopify:section:reorder'].forEach((type) =>
    document.addEventListener(type, () => setTimeout(scan))
  );

  window.themeMotion = { scan };
  sync();
})();
